import ReactEcharts from "echarts-for-react";
import { EChartsOption } from "echarts-for-react";
import { useAppSelector } from "../../../../app/hooks";
import { miwChartColors, tooltipMark, utahChartColors } from "../../shared/reportUtil";
import { GenreScoreViewModel } from "./performanceReportApiSlice";

interface ChartData {
	name: string;
	score: number;
}

function initializeOptions(
	data: ChartData[],
	dataLabel: string,
	average: number,
	averageLabel: string,
	maxValue: number,
	yAxisInterval: number
): EChartsOption {
	const { config } = useAppSelector((state) => state.site);
	let chartColors = config?.isUtahComposeSite ? utahChartColors : miwChartColors;

	const { foregroundColor, chartPalette, accentColor } = useAppSelector(
		(state) => state.contrast
	);

	if (chartPalette) {
		chartColors = chartPalette.slice(0, 2);
	}

	const option: EChartsOption = {
		textStyle: {
			color: foregroundColor,
			fontFamily: "Montserrat",
			fontWeight: "500",
		},
		grid: {
			containLabel: true,
			top: 50,
			left: 30,
			right: 20,
			bottom: 25,
		},
		yAxis: {
			type: "value",
			min: 0,
			max: maxValue,
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
			name: dataLabel,
			nameLocation: "middle",
			nameGap: 30,
			interval: yAxisInterval,
			splitLine: {
				show: true,
				lineStyle: {
					color: accentColor,
				},
			},
		},
		xAxis: {
			type: "category",
			data: data.map((d) => d.name),
			axisLabel: {
				margin: 20,
				width: 100,
				lineHeight: 15,
				interval: 0,
				overflow: "break",
				hideOverlap: false,
			},
			axisLine: {
				show: true,
				lineStyle: {
					color: foregroundColor,
				},
			},
		},
		tooltip: {
			trigger: "item",
			formatter: function (params: { value: string; name: string; color: string }) {
				const heading = `<div>${params.name}</div>`;
				const lineItem = `<div>${tooltipMark(params.color)} Average: <b>${params.value}</b></div>`;
				const avgLine = `<div>${tooltipMark("red")} Student Average: <b>${average}</b></div>`;
				return heading + lineItem + avgLine;
			},
			textStyle: {
				fontSize: 13,
			},
		},
		legend: {
			data: [
				{
					name: "Genre Averages",
					itemStyle: {
						color: chartColors[0],
					},
				},
				{
					name: averageLabel,
					icon: "circle",
				},
			],
			show: true,
			padding: 10,
			itemGap: 20,
			textStyle: {
				color: foregroundColor,
			},
			top: 0,
		},
		animationDurationUpdate: 500,
		series: [
			{
				type: "bar",
				id: "category",
				name: "Genre Averages",
				data: data.map((d) => ({
					value: d.score,
				})),
				itemStyle: {
					color: function (param: { dataIndex: number }) {
						return chartColors[param.dataIndex % chartColors.length];
					},
				},
				label: {
					show: true,
					position: ["50%", "0%"],
					height: 25,
					width: 25,
					align: "center",
					verticalAlign: "middle",
					padding: 4,
					backgroundColor: "#fff",
					borderRadius: 25,
					borderColor: "inherit",
					borderWidth: 1,
					fontWeight: "bolder",
					fontSize: 12,
				},
				barMinWidth: 40,
				emphasis: {
					itemStyle: {
						shadowBlur: 10,
					},
				},
				cursor: "default",
			},
			{
				name: averageLabel,
				type: "line",
				color: "red",
				symbol: "none",
				markLine: {
					name: "Average Line",
					data: [
						{
							yAxis: average,
						},
					],
					label: {
						show: false,
					},
					lineStyle: {
						color: "red",
					},
					symbol: ["circle", "circle"],
					silent: true,
				},
			},
		],
	};
	return option;
}

interface Props {
	className?: string;
	data: GenreScoreViewModel;
}

export function GenreScoreChart(props: Props) {
	if (
		!props.data ||
		props.data.averageScore == null ||
		(props.data.argumentativeScore == null &&
			props.data.informativeScore == null &&
			props.data.narrativeScore == null)
	)
		return "";

	const chartData = [
		{
			name: "Argumentative",
			score: props.data.argumentativeScore,
		},
		{
			name: "Informative/Explanatory",
			score: props.data.informativeScore,
		},
		{
			name: "Narrative",
			score: props.data.narrativeScore,
		},
	];

	const options = initializeOptions(
		chartData,
		"Average",
		props.data.averageScore,
		"Overall Average",
		30,
		5
	);

	return <ReactEcharts option={options} className={`w-full !h-96 ${props.className}`} />;
}
